import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-register-card.css?inline';
import '../pk-card/pk-card.js';

/**
 * `<pk-register-card>` — portal registration form surface.
 *
 * Wraps a `<pk-card>` and renders the standard registration field set:
 * email, password, confirm-password (client-side match-validated),
 * company-name, an agree-to-terms checkbox with linked Terms / Privacy,
 * an optional hidden invite token, an optional error alert, and a foot
 * slot for secondary links ("Have an account? Sign in").
 *
 * The form submits via standard browser POST. Field names follow
 * camelCase and bind case-insensitively to ASP.NET Core PascalCase
 * properties (`email` -> `Email`, `companyName` -> `CompanyName`, ...).
 *
 * Attributes:
 *   - `action`           — form POST URL (default: `/portal/register`)
 *   - `invite`           — invite token; written as `inviteToken` hidden input
 *   - `email`            — pre-fill email
 *   - `company-name`     — pre-fill company / organization name
 *   - `error`            — error message; renders a `<pk-alert>` above the form
 *   - `csrf-token`       — anti-forgery token; renders a hidden input
 *   - `csrf-name`        — CSRF hidden-input name (default `__RequestVerificationToken`)
 *   - `eyebrow`          — uppercase brand label. Default: `ActivityLog`
 *   - `title`            — heading. Default: `Create your account`
 *   - `lede`             — paragraph beneath the heading. Default invites the user
 *   - `terms-url`        — URL for the Terms of Service link (default `/terms-of-service`)
 *   - `privacy-url`      — URL for the Privacy Policy link (default `/privacy-policy`)
 *   - `min-password-length` — minimum password length (default `10`)
 *   - `readonly-email`   — boolean; when set, the email field is rendered
 *                          as a static, non-editable display (with a hidden
 *                          input carrying the value) so the form still POSTs
 *                          the email. Used for invited registration where the
 *                          email is bound to the invite and must not be
 *                          changed.
 *   - `hide-company-name` — boolean; when set, the companyName field is
 *                          omitted entirely (no input, no hidden input).
 *                          Used for invited registration where the invitee
 *                          joins an existing tenant and has no new company
 *                          to name. Pairs with `readonly-email`; the
 *                          server-side handler for the invited path simply
 *                          doesn't read `companyName`.
 *
 * Slots:
 *   - `invite-preview` — invited registration metadata rendered above the
 *                        form (tenant name, role, expiry). Empty by default.
 *   - `foot`           — links beneath the form ("Have an account? Sign in")
 */
export class PkRegisterCard extends HTMLElement {
    static readonly tagName = 'pk-register-card';

    static get observedAttributes(): string[] {
        return [
            'action', 'invite', 'email', 'company-name', 'error',
            'csrf-token', 'csrf-name',
            'eyebrow', 'title', 'lede',
            'terms-url', 'privacy-url',
            'min-password-length',
            'readonly-email', 'hide-company-name',
        ];
    }

    private readonly _root: ShadowRoot;
    private _submitting = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this._root.addEventListener('submit', this._handleSubmit);
        this._root.addEventListener('input',  this._handlePwInput);
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._handleSubmit);
        this._root.removeEventListener('input',  this._handlePwInput);
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private readonly _handleSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        if (!form.classList.contains('register-form')) return;

        // Client-side password-match validation. pk-form-field forwards
        // setCustomValidity through an inner method we don't have direct
        // access to from the parent shadow root, so we just block submission
        // and surface a pk-alert if they don't match.
        const pwField  = this._root.querySelector('pk-form-field[name="password"]') as HTMLElement & { value?: string };
        const cpwField = this._root.querySelector('pk-form-field[name="confirmPassword"]') as HTMLElement & { value?: string };
        if (pwField && cpwField && pwField.value !== cpwField.value) {
            e.preventDefault();
            this.setAttribute('error', 'Passwords do not match.');
            return;
        }

        this._submitting = true;
        this.render();
    };

    private readonly _handlePwInput = (): void => {
        // Clear the password-mismatch error once the user edits either field.
        if ((this.getAttribute('error') ?? '') === 'Passwords do not match.') {
            this.removeAttribute('error');
        }
    };

    private get _action(): string {
        return this.getAttribute('action') ?? '/portal/register';
    }

    private get _invite(): string {
        return this.getAttribute('invite') ?? '';
    }

    private get _email(): string {
        return this.getAttribute('email') ?? '';
    }

    private get _companyName(): string {
        return this.getAttribute('company-name') ?? '';
    }

    private get _error(): string {
        return this.getAttribute('error') ?? '';
    }

    private get _csrfToken(): string {
        return this.getAttribute('csrf-token') ?? '';
    }

    private get _csrfName(): string {
        return this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
    }

    private get _eyebrow(): string {
        return this.getAttribute('eyebrow') ?? 'ActivityLog';
    }

    private get _title(): string {
        return this.getAttribute('title') ?? 'Create your account';
    }

    private get _lede(): string {
        return this.getAttribute('lede') ?? 'Get started — it only takes a minute.';
    }

    private get _termsUrl(): string {
        return this.getAttribute('terms-url') ?? '/terms-of-service';
    }

    private get _privacyUrl(): string {
        return this.getAttribute('privacy-url') ?? '/privacy-policy';
    }

    private get _minPasswordLength(): number {
        const raw = this.getAttribute('min-password-length');
        const n   = raw ? Number.parseInt(raw, 10) : Number.NaN;
        return Number.isFinite(n) && n > 0 ? n : 10;
    }

    private template(): TemplateResult {
        const action      = this._action;
        const invite      = this._invite;
        const email       = this._email;
        const companyName = this._companyName;
        const error       = this._error;
        const csrfToken   = this._csrfToken;
        const csrfName    = this._csrfName;
        const eyebrow     = this._eyebrow;
        const heading     = this._title;
        const lede        = this._lede;
        const termsUrl    = this._termsUrl;
        const privacyUrl  = this._privacyUrl;
        const minPwLen    = this._minPasswordLength;
        const submitting  = this._submitting;

        return html`
            <style>${styles}</style>
            <pk-card>
                <span slot="eyebrow" class="eyebrow">${eyebrow}</span>
                <h1>${heading}</h1>
                <p class="lede">${lede}</p>

                ${error ? html`
                    <pk-alert variant="error" class="form-error">${error}</pk-alert>
                ` : ''}

                <div class="invite-preview">
                    <slot name="invite-preview"></slot>
                </div>

                <form
                    class="register-form"
                    method="post"
                    action=${action}
                    novalidate
                >
                    ${csrfToken ? html`
                        <input type="hidden" name=${csrfName} .value=${csrfToken} />
                    ` : ''}
                    ${invite ? html`
                        <input type="hidden" name="inviteToken" .value=${invite} />
                    ` : ''}

                    ${this.hasAttribute('readonly-email')
                        ? html`
                            <div class="email-readonly" role="group" aria-label="Email address">
                                <span class="email-readonly-label">Email address</span>
                                <span class="email-readonly-value">${email}</span>
                                <input type="hidden" name="email" .value=${email} />
                            </div>
                        `
                        : html`
                            <pk-form-field
                                name="email"
                                type="email"
                                label="Email address"
                                autocomplete="username"
                                .value=${email}
                                required
                            ></pk-form-field>
                        `
                    }

                    <pk-form-field
                        name="password"
                        type="password"
                        label="Password"
                        hint="${minPwLen}+ characters"
                        autocomplete="new-password"
                        minlength=${minPwLen}
                        required
                    ></pk-form-field>

                    <pk-form-field
                        name="confirmPassword"
                        type="password"
                        label="Confirm password"
                        autocomplete="new-password"
                        minlength=${minPwLen}
                        required
                    ></pk-form-field>

                    ${this.hasAttribute('hide-company-name')
                        ? ''
                        : html`
                            <pk-form-field
                                name="companyName"
                                type="text"
                                label="Company / organization name"
                                autocomplete="organization"
                                .value=${companyName}
                                required
                            ></pk-form-field>
                        `
                    }

                    <label class="terms">
                        <input type="checkbox" name="agreeToTerms" value="true" required />
                        <span>
                            I agree to the
                            <a href=${termsUrl} target="_blank" rel="noopener">Terms of Service</a>
                            and
                            <a href=${privacyUrl} target="_blank" rel="noopener">Privacy Policy</a>.
                        </span>
                    </label>

                    <pk-button
                        variant="primary"
                        type="submit"
                        class="submit-btn"
                        ?data-submitting=${submitting}
                        loading-label="Creating account…"
                        ?disabled=${submitting}
                        aria-busy=${submitting ? 'true' : 'false'}
                    >Create account</pk-button>
                </form>

                <div slot="foot">
                    <slot name="foot">
                        <span>Have an account? <a href="/portal/login">Sign in</a></span>
                    </slot>
                </div>
            </pk-card>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkRegisterCard.tagName)) {
    customElements.define(PkRegisterCard.tagName, PkRegisterCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-register-card': PkRegisterCard;
    }
}
